import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Detect Safari browser (including iOS Safari)
 * Safari has known issues with MediaRecorder that require specific handling
 */
const isSafari = () => {
  const ua = navigator.userAgent.toLowerCase();
  const isSafariBrowser = ua.includes('safari') && !ua.includes('chrome') && !ua.includes('chromium');
  const isIOS = /ipad|iphone|ipod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return isSafariBrowser || isIOS;
};

/**
 * Get Safari-compatible MediaRecorder options
 * Safari supports limited codecs and behaves differently than Chrome/Firefox
 */
const getSafariMediaRecorderOptions = () => {
  // Safari prefers MP4 with H.264/AAC - check if supported
  const mimeTypes = [
    'video/mp4;codecs=avc1,mp4a.40.2',
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/mp4',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];

  for (const mimeType of mimeTypes) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      return { mimeType };
    }
  }

  return {};
};

export const RecorderErrors = {
  AbortError: 'media_aborted',
  NotAllowedError: 'permission_denied',
  NotFoundError: 'no_specified_media_found',
  NotReadableError: 'media_in_use',
  OverconstrainedError: 'invalid_media_constraints',
  TypeError: 'no_constraints',
  NONE: '',
  NO_RECORDER: 'recorder_error',
  EMPTY_RECORDING: 'empty_recording',
}

export function useReactMediaRecorder({
  audio = true,
  video = false,
  onStop = () => null,
  blobPropertyBag,
  screen = false,
  mediaRecorderOptions = null,
}) {
  const mediaRecorder = useRef(null);
  const mediaChunks = useRef([]);
  const mediaStream = useRef(null);
  const [status, setStatus] = useState('idle');
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [mediaBlobUrl, setMediaBlobUrl] = useState(null);
  const [error, setError] = useState('NONE');

  const getMediaStream = useCallback(async () => {
    setStatus('acquiring_media');
    const requiredMedia = {
      audio: typeof audio === 'boolean' ? !!audio : audio,
      video: typeof video === 'boolean' ? !!video : video,
    };
    try {
      if (screen) {
        const stream = (await window.navigator.mediaDevices.getDisplayMedia({
          video: video || true,
        }));
        if (audio) {
          const audioStream = await window.navigator.mediaDevices.getUserMedia({
            audio,
          });

          audioStream
            .getAudioTracks()
            .forEach((audioTrack) => stream.addTrack(audioTrack));
        }
        mediaStream.current = stream;
      } else {
        const stream = await window.navigator.mediaDevices.getUserMedia(
          requiredMedia
        );
        mediaStream.current = stream;
      }
      setStatus('idle');
    } catch (error) {
      setError(error.name);
      setStatus('idle');
    }
  }, [audio, video, screen]);

  useEffect(() => {
    if (!window.MediaRecorder) {
      throw new Error('Unsupported Browser');
    }

    if (screen) {
      if (!window.navigator.mediaDevices.getDisplayMedia) {
        throw new Error('This browser does not support screen capturing');
      }
    }

    const checkConstraints = (mediaType) => {
      const supportedMediaConstraints = navigator.mediaDevices.getSupportedConstraints();
      const unSupportedConstraints = Object.keys(mediaType).filter(
        (constraint) =>
          !(supportedMediaConstraints)[constraint]
      );

      if (unSupportedConstraints.length > 0) {
        // eslint-disable-next-line no-console
        console.error(
          `The constraints ${unSupportedConstraints.join(
            ','
          )} doesn't support on this browser. Please check your ReactMediaRecorder component.`
        );
      }
    };

    if (typeof audio === 'object') {
      checkConstraints(audio);
    }
    if (typeof video === 'object') {
      checkConstraints(video);
    }

    if (mediaRecorderOptions && mediaRecorderOptions.mimeType) {
      if (!MediaRecorder.isTypeSupported(mediaRecorderOptions.mimeType)) {
        // eslint-disable-next-line no-console
        console.error(
          `The specified MIME type you supplied for MediaRecorder doesn't support this browser`
        );
      }
    }

    if (!mediaStream.current) {
      getMediaStream();
    }
  }, [audio, screen, video, getMediaStream, mediaRecorderOptions]);

  // Media Recorder Handlers

  const startRecording = async () => {
    setError('NONE');
    if (!mediaStream.current) {
      await getMediaStream();
    }
    if (mediaStream.current) {
      const isStreamEnded = mediaStream.current
        .getTracks()
        .some((track) => track.readyState === 'ended');
      if (isStreamEnded) {
        await getMediaStream();
      }

      mediaChunks.current = [];

      // Use Safari-specific options when on Safari
      const safariMode = isSafari();
      const recorderOptions = safariMode
        ? { ...getSafariMediaRecorderOptions(), ...mediaRecorderOptions }
        : mediaRecorderOptions || {};

      try {
        mediaRecorder.current = new MediaRecorder(mediaStream.current, recorderOptions);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('MediaRecorder creation failed with options, trying without:', err);
        mediaRecorder.current = new MediaRecorder(mediaStream.current);
      }

      mediaRecorder.current.ondataavailable = onRecordingActive;
      mediaRecorder.current.onstop = onRecordingStop;
      mediaRecorder.current.onerror = (event) => {
        // eslint-disable-next-line no-console
        console.error('MediaRecorder error:', event.error);
        setError('NO_RECORDER');
        setStatus('idle');
      };

      // Use timeslice to collect chunks periodically (1 second)
      // This prevents data loss on Safari and helps with memory on mobile devices
      mediaRecorder.current.start(1000);
      setStatus('recording');
    }
  };

  const onRecordingActive = ({ data }) => {
    if (data && data.size > 0) {
      mediaChunks.current.push(data);
    }
  };

  const onRecordingStop = () => {
    if (!mediaChunks.current || mediaChunks.current.length === 0) {
      // eslint-disable-next-line no-console
      console.error('Recording failed: No media chunks captured');
      setError('EMPTY_RECORDING');
      setStatus('idle');
      onStop(null, null);
      return;
    }

    const [chunk] = mediaChunks.current;
    const blobProperty = Object.assign(
      { type: chunk?.type || 'video/mp4' },
      blobPropertyBag || (video ? { type: 'video/mp4' } : { type: 'audio/wav' })
    );
    const blob = new Blob(mediaChunks.current, blobProperty);

    if (blob.size < 1000) {
      // eslint-disable-next-line no-console
      console.error('Recording failed: Blob too small', blob.size);
      setError('EMPTY_RECORDING');
      setStatus('idle');
      onStop(null, null);
      return;
    }

    // Log recording info for debugging
    if (window.newrelic) {
      window.newrelic.addPageAction('VIDEO_RECORDING_COMPLETED', {
        blobSize: blob.size,
        chunksCount: mediaChunks.current.length,
        browser: isSafari() ? 'Safari' : 'Other',
      });
    }

    const url = URL.createObjectURL(blob);
    setStatus('stopped');
    setMediaBlobUrl(url);
    onStop(url, blob);
  };

  const muteAudio = (mute) => {
    setIsAudioMuted(mute);
    if (mediaStream.current) {
      mediaStream.current
        .getAudioTracks()
        .forEach((audioTrack) => (audioTrack.enabled = !mute));
    }
  };

  const pauseRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.pause();
    }
  };
  const resumeRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'paused') {
      mediaRecorder.current.resume();
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current) {
      if (mediaRecorder.current.state !== 'inactive') {
        setStatus('stopping');

        // Safari fix: Request any pending data before stopping
        // This ensures we capture all remaining chunks that might be buffered
        if (isSafari() && mediaRecorder.current.state === 'recording') {
          try {
            // requestData() triggers ondataavailable with any buffered data
            mediaRecorder.current.requestData();
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('requestData failed (non-critical):', err);
          }

          // Small delay to allow Safari to flush the data before stopping
          setTimeout(() => {
            if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
              mediaRecorder.current.stop();
              mediaStream.current &&
                mediaStream.current.getTracks().forEach((track) => track.stop());
            }
          }, 100);
        } else {
          mediaRecorder.current.stop();
          mediaStream.current &&
            mediaStream.current.getTracks().forEach((track) => track.stop());
        }
      }
    }
  };

  const resetRecording = () => {
    setMediaBlobUrl(null);
    mediaChunks.current = [];
    getMediaStream();
  }

  return {
    error: RecorderErrors[error],
    muteAudio: () => muteAudio(true),
    unMuteAudio: () => muteAudio(false),
    startRecording,
    pauseRecording,
    resumeRecording,
    resetRecording,
    stopRecording,
    mediaBlobUrl,
    status,
    isAudioMuted,
    previewStream: mediaStream.current
      ? new MediaStream(mediaStream.current.getVideoTracks())
      : null,
    clearBlobUrl: () => setMediaBlobUrl(null),
  };
}

export const ReactMediaRecorder = (props) =>
  props.render(useReactMediaRecorder(props));



// WEBPACK FOOTER //
// ./src/hooks/useMediaRecorder.js